import Ajv from "ajv";
import addFormats from "ajv-formats";
import { betterAjvErrors } from "@apideck/better-ajv-errors";
import { SAFE_CSS_PROPERTIES } from "@basis-theory/web-elements";
import { ODIN_INPUT_CLASSNAME } from "./util/constants";

export const kebabize = (str) => {
	return str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? "-" : "") + $.toLowerCase());
};

/**
 * Error strings for development purposes.
 */
export const Errors = Object.freeze({
	BUTTON_MISSING: "HTML button element not found",
	ODIN_ERROR: "OdinPay Error: ",
	CONFIGURATION: "Configuration error.  Please check your Odin Pay configuration object.",
	CARD_FIELD_MISSING: "Card HTML container not found.",
	MISSING_API_KEY: "Missing API Key.",
	SUBMIT_BUTTON_MISSING: "Submit button HTML not found."
});

/**
 * Possible selectors to inject Javascript into HTML elements.
 * @type {Readonly<{ODIN_ACCOUNT_NUMBER: string, ODIN_ROUTING_NUMBER: string, ODIN_CARD: string, ODIN_SUBMIT: string}>}
 */
export const HTMLId = Object.freeze({
	ODIN_CARD: "odin-card",
	ODIN_ROUTING_NUMBER: "odin-routing-number",
	ODIN_ACCOUNT_NUMBER: "odin-account-number",
	ODIN_SUBMIT: "odin-submit"
});

/**
 * @param {ObjectSchema} schema
 * @param {Object} data - the data to validate
 * @param {boolean} [returnPropertyNames]
 * @return {ValidationReturn}
 */

export const validate = (schema, data, returnPropertyNames) => {
	const ajv = new Ajv({
		allErrors: true,
		verbose: true
	});
	addFormats(ajv);
	ajv.addKeyword({
		keyword: "disallowEmptyString",
		type: "string",
		validate: (s, d) => {
			if (s) {
				return typeof d === "string" && d.trim() !== "";
			}
			return true;
		}
	});
	/*
	ajv.addKeyword({
		keyword: "typeof",
		validate: (s, d) => {
			if (s) {
				return typeof d === "string" && d.trim() !== "";
			}
			return true;
		}
	});
	*/
	const validator = ajv.compile(schema);
	const isValid = validator(data);
	const { errors } = validator;
	let parsedErrors = null;
	if (errors) {
		parsedErrors = betterAjvErrors({ schema, data, errors }).map((error) =>
			error.message.replace("{base}", schema.title)
		);
	}
	let validation = { isValid, errors: parsedErrors };
	if (returnPropertyNames) {
		let errorPropertyNames = [];

		if (errors && errors.length) {
			errorPropertyNames = errors.reduce((accumulator, error) => {
				if (error.instancePath) {
					return [...accumulator, error.instancePath.slice(1)];
				}
				if (error.params && Object.values(error.params).length) {
					return [...accumulator, Object.values(error.params)[0]];
				}
				return accumulator;
			}, []);
		}

		validation = { ...validation, propertyNames: errorPropertyNames };
	}

	return validation;
};

/**
 * @type {odinTheme}
 */
export const DEFAULT_THEME = {
	color: "black",
	fontFamily: "Intero",
	padding: "5px"
};

/**
 * gets the safe styles from the theme
 * @param {styleObject} themeSection
 */
export const getSafeStyles = (themeSection) => {
	return Object.entries(themeSection).reduce((accumulator, [key, value]) => {
		if (SAFE_CSS_PROPERTIES.includes(key)) {
			return {
				...accumulator,
				[key]: value
			};
		}
		return accumulator;
	}, {});
};

/**
 * gets the pseudo element styles from the theme and parses them in a way that can be added to the stylesheet.
 * @param {styleObject} inputStyles
 * @return {String}
 */
export const getPseudoElementStyles = (inputStyles) => {
	/** based off the allowed pseudo elements in Basis Theory */
	const pseudoElementStyles = [":hover", ":focus", ":read-only", "::placeholder", "::selection", ":disabled"];
	return Object.entries(inputStyles).reduce((accumulator, [key, value]) => {
		if (pseudoElementStyles.includes(key) && typeof value === "object") {
			const styles = Object.entries(value).reduce((acc, [k, v]) => {
				return `${acc} ${kebabize(k)}: ${v};`;
			}, "");
			return `${accumulator} .${ODIN_INPUT_CLASSNAME}${key} { ${styles} }`;
		}
		return accumulator;
	}, "");
};

export const handlePseudoElementStyles = (customTheme) => {
	const pseudoElementStyles = getPseudoElementStyles(customTheme);
	if (pseudoElementStyles) {
		const style = document.createElement("style");
		style.appendChild(document.createTextNode(pseudoElementStyles));
		const head = document.getElementsByTagName("head")[0];
		head.appendChild(style);
	}
};

/**
 * Takes a JS style object and returns a CSS string
 * @param {styleObject} theme
 * @returns {string}
 */
export const parseTheme = (theme) => {
	/** these styles are on the basis theory iframe, so we want to match those with our own elements */
	const basisTheoryIframeStyles = {
		borderStyle: "none !important",
		display: "block !important",
		height: "29px !important",
		margin: "0px !important",
		minWidth: "100% !important",
		overflow: "hidden !important",
		padding: "0px !important",
		userSelect: "none !important",
		width: "1px !important",
		willChange: "transform !important"
	};

	const additionalStyles = {
		outline: "none !important",
		minHeight: "auto !important"
	};

	/**
	 * adding certain overrides for the inputs, to match Basis Theory and make sure all CSS styling is done to the div containers rather than the inputs themselves.  Any direct input styling should be through the Theme object.
	 */
	const updatedTheme = {
		...theme,
		...basisTheoryIframeStyles,
		...additionalStyles
	};
	/**
	 * A string representing styles X, to be applied via "setAttribute("style", X)"
	 * @type {string}
	 */
	const parsedTheme = Object.entries(updatedTheme).reduce((accumulator, [key, value]) => {
		return `${accumulator} ${kebabize(key)}: ${value};`;
	}, "");
	return parsedTheme || "";
};

export const FIELD_NAMES = Object.freeze({
	ACCOUNT_NUMBER: "accountNumber",
	ADDRESS_LINE_1: "addressLine1",
	ADDRESS_LINE_2: "addressLine2",
	CARD_INFORMATION: "cardInformation",
	CITY: "city",
	COUNTRY: "country",
	EMAIL_ADDRESS: "emailAddress",
	INSTITUTION_NUMBER: "institutionNumber",
	NAME: "name",
	PHONE_NUMBER: "phoneNumber",
	POSTAL_CODE: "postalCode",
	ROUTING_NUMBER: "routingNumber",
	STATE: "state",
	TRANSIT_NUMBER: "transitNumber"
});

export const ERROR_TYPES = Object.freeze({
	BACKEND: "BACKEND",
	FIELD_VALIDATION: "FIELD_VALIDATION"
});

export const ERROR_CODES = Object.freeze({
	INVALID: "INVALID",
	INVALID_CARD_INFORMATION: "INVALID_CARD_INFORMATION",
	INVALID_BANK_INFORMATION: "INVALID_BANK_INFORMATION",
	TOKENIZATION_FAILED: "TOKENIZATION_FAILED",
	MAX_LENGTH_EXCEEDED: "MAX_LENGTH_EXCEEDED",
	REQUIRED: "REQUIRED",
	UNAUTHORIZED: "UNAUTHORIZED",
	VALIDATION_ERROR: "VALIDATION_ERROR"
});

/**
 * @typedef {Object} OdinError - odin unauthorized and BT errors will only have statusCode and message
 * @property {Number} [code]
 * @property { { errors: String[] } } [data]
 * @property {String} message
 * @property {Number} statusCode
 * @property {String} [type]
 */

/**
 * @typedef {Object} OdinForbiddenError
 * @type {
 */

/**
 * @typedef {Object} FieldValidation
 * @property {String} type
 * @property {String} fieldName
 * @property {String} [errorCode]
 * @property {String} selector
 * @property {Boolean} isComplete
 * @property {Boolean} isValid
 */

/**
 * @param {Boolean} isComplete
 * @param {Boolean} isValid
 * @param {String} fieldName
 * @param {String} selector
 * @param {String} [statusCode]
 * @returns {FieldValidation}
 * @constructor
 */
export const buildValidationObject = ({ isComplete, isValid, fieldName, selector, errorCode }) => {
	let errorObject = {
		type: ERROR_TYPES.FIELD_VALIDATION,
		fieldName,
		selector,
		isValid,
		isComplete
	};
	if (errorCode) {
		errorObject = {
			...errorObject,
			errorCode
		};
	}
	return errorObject;
};
